// Modal de «Actualizar desde Zentral»: calcula en modo prueba, muestra la vista previa y aplica.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReconciliationPreviewItem } from '@fiado/type-kit/bin/retailIntegration/dtos/ReconciliationPreview';
import type { ReconciliationRunCounts } from '@fiado/type-kit/bin/retailIntegration/dtos/ReconciliationRunCounts';
import { ReconciliationEntityEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationEntityEnum';
import { ReconciliationRunStatusEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationRunStatusEnum';
import type { RetailIntegrationModeEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailIntegrationModeEnum';
import { Button, Loading, Modal, Select } from '@/kit';
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import {
    useReconciliationRun,
    type ReconcileTarget,
    type ReconciliationRun,
    type ReconciliationRunState,
} from '../api/useReconciliation';
import {
    appliesDeactivations,
    changesToApply,
    hiddenRows,
    PREVIEW_GROUPS,
    previewFieldKeys,
    previewItemName,
    sortPreviewItems,
    type PreviewGroup,
} from '../lib/reconciliationPreview';
import { abortKindOf, DEFAULT_MAX_ATTEMPTS, isZentralDown } from '../lib/reconciliation';

type Translate = ReturnType<typeof useTranslation>['t'];

export interface StoreOption {
    storeId: string;
    name: string;
}

export interface ReconcilePreviewDialogProps {
    /** Controlado: el modal queda montado para que el foco regrese al botón al cerrar. */
    isOpen: boolean;
    retailerId: string;
    target: ReconcileTarget;
    mode: RetailIntegrationModeEnum | null;
    /** Inventario de una tienda: el modal primero pide elegirla. */
    chooseStore?: boolean;
    /** Tiendas de la cadena: nombres de los renglones de inventario y opciones para elegir. */
    stores?: readonly StoreOption[];
    onClose: () => void;
    /** Se llama una vez cuando la corrida real termina COMPLETE. */
    onApplied?: () => void;
}

export function ReconcilePreviewDialog({ isOpen, onClose, ...props }: ReconcilePreviewDialogProps) {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            {isOpen ? <DialogBody {...props} onClose={onClose} /> : null}
        </Modal>
    );
}

/** Contenido del modal; se desmonta al cerrar, así se detiene el polling y se olvida la tienda. */
function DialogBody({
    retailerId,
    target,
    mode,
    chooseStore = false,
    stores = [],
    onClose,
    onApplied,
}: Omit<ReconcilePreviewDialogProps, 'isOpen'>) {
    const { t } = useTranslation('reconciliation');
    const [chosenStore, setChosenStore] = useState<string | null>(null);
    const storeNames = new Map(stores.map((store) => [store.storeId, store.name]));
    const resolved: ReconcileTarget | null =
        chooseStore && target.entity === ReconciliationEntityEnum.INVENTORY
            ? chosenStore === null
                ? null
                : { entity: ReconciliationEntityEnum.INVENTORY, storeId: chosenStore }
            : target;
    const storeId =
        resolved?.entity === ReconciliationEntityEnum.INVENTORY ? resolved.storeId : undefined;
    const title = storeId
        ? t('dialog.titleStore', { store: storeNames.get(storeId) ?? storeId })
        : t('dialog.title', { entity: t(`entity.${target.entity}`) });

    return (
        <>
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                {mode ? <DialogDescription>{t('dialog.mode', { mode })}</DialogDescription> : null}
            </DialogHeader>
            {resolved === null ? (
                <StoreChooser stores={stores} onChoose={setChosenStore} onCancel={onClose} />
            ) : (
                <PreviewFlow
                    retailerId={retailerId}
                    target={resolved}
                    mode={mode}
                    storeNames={storeNames}
                    onClose={onClose}
                    onApplied={onApplied}
                />
            )}
        </>
    );
}

function StoreChooser({
    stores,
    onChoose,
    onCancel,
}: {
    stores: readonly StoreOption[];
    onChoose: (storeId: string) => void;
    onCancel: () => void;
}) {
    const { t } = useTranslation('reconciliation');
    const [value, setValue] = useState('');
    const options = [...stores]
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
        .map((store) => ({ value: store.storeId, label: store.name }));
    return (
        <div className="flex flex-col gap-3">
            <Select
                label={t('choose.label')}
                placeholder={t('choose.placeholder')}
                value={value}
                onChange={setValue}
                options={options}
            />
            <p className="text-xs text-muted-foreground">{t('choose.hint')}</p>
            <Footer>
                <Button variant="outline" onClick={onCancel}>
                    {t('actions.cancel')}
                </Button>
                <Button
                    disabled={value === ''}
                    onClick={() => {
                        onChoose(value);
                    }}
                >
                    {t('choose.submit')}
                </Button>
            </Footer>
        </div>
    );
}

type Phase =
    | { kind: 'calculating'; run: ReconciliationRun | undefined }
    | { kind: 'preview'; run: ReconciliationRun }
    | { kind: 'applying'; run: ReconciliationRun | undefined }
    | { kind: 'applied'; run: ReconciliationRun }
    | { kind: 'error'; message: string; code: string | null; retry: (() => void) | null };

interface RunError {
    message: string;
    /** Code crudo que se muestra en chico cuando el motivo no tiene traducción. */
    code: string | null;
}

/** Motivo de una corrida ABORTED, traducido según `abortReason`. */
function abortedErrorOf(t: Translate, run: ReconciliationRun): RunError {
    const kind = abortKindOf(run.abortReason);
    const maxAttempts = run.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    if (kind === 'unknown') return { message: t('errors.aborted'), code: null };
    // MAX_ATTEMPTS también sale por errores internos: solo se culpa a Zentral si el último code es suyo.
    if (kind === 'maxAttempts' && !isZentralDown(run.lastErrorCode)) {
        return {
            message: t('errors.abort.maxAttemptsFailed', { maxAttempts }),
            code: run.lastErrorCode ?? null,
        };
    }
    return {
        message: t(`errors.abort.${kind}`, { maxAttempts }),
        code: kind === 'other' ? (run.abortReason ?? null) : null,
    };
}

/** Error de una corrida que no terminó bien; `null` si va bien o sigue abierta. */
function runErrorOf(t: Translate, state: ReconciliationRunState, entity: string): RunError | null {
    const plain = (message: string): RunError => ({ message, code: null });
    if (state.failure) {
        if (state.failure.kind === 'alreadyRunning') {
            return plain(t('errors.alreadyRunning', { entity }));
        }
        return plain(t(`errors.${state.failure.kind}`));
    }
    if (state.pollFailed) return plain(t('errors.pollFailed'));
    if (state.run?.status === ReconciliationRunStatusEnum.ABORTED) {
        return abortedErrorOf(t, state.run);
    }
    if (state.run?.status === ReconciliationRunStatusEnum.BRAKED) return plain(t('errors.braked'));
    return null;
}

/** Una corrida frenada por bajas de más no se reintenta desde aquí: la ve soporte. */
function retryOf(state: ReconciliationRunState): (() => void) | null {
    return state.run?.status === ReconciliationRunStatusEnum.BRAKED ? null : state.retry;
}

function phaseOf(
    t: Translate,
    entity: string,
    dry: ReconciliationRunState,
    apply: ReconciliationRunState,
): Phase {
    const applyStarted = apply.isStarting || apply.runId !== null || apply.failure !== null;
    if (applyStarted) {
        const error = runErrorOf(t, apply, entity);
        if (error) return { kind: 'error', ...error, retry: retryOf(apply) };
        if (apply.run?.status === ReconciliationRunStatusEnum.COMPLETE) {
            return { kind: 'applied', run: apply.run };
        }
        return { kind: 'applying', run: apply.run };
    }
    const error = runErrorOf(t, dry, entity);
    if (error) return { kind: 'error', ...error, retry: retryOf(dry) };
    if (dry.run?.status === ReconciliationRunStatusEnum.COMPLETE) {
        return { kind: 'preview', run: dry.run };
    }
    return { kind: 'calculating', run: dry.run };
}

function PreviewFlow({
    retailerId,
    target,
    mode,
    storeNames,
    onClose,
    onApplied,
}: {
    retailerId: string;
    target: ReconcileTarget;
    mode: RetailIntegrationModeEnum | null;
    storeNames: ReadonlyMap<string, string>;
    onClose: () => void;
    onApplied?: () => void;
}) {
    const { t } = useTranslation('reconciliation');
    const dry = useReconciliationRun(retailerId, target, true);
    const apply = useReconciliationRun(retailerId, target, false);

    // Abre la corrida de prueba una sola vez al montar.
    const started = useRef(false);
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        dry.start();
    }, [dry]);

    const entity = t(`entity.${target.entity}`);
    const phase = phaseOf(t, entity, dry, apply);
    useAppliedCallback(phase.kind === 'applied' ? phase.run.runId : null, onApplied);

    switch (phase.kind) {
        case 'calculating':
            return <Calculating run={phase.run} onCancel={onClose} />;
        case 'preview':
            return (
                <PreviewStep
                    run={phase.run}
                    entity={target.entity}
                    mode={mode}
                    storeNames={storeNames}
                    onCancel={onClose}
                    onApply={apply.start}
                />
            );
        case 'applying':
            return (
                <div className="flex flex-col gap-2">
                    <Progress heading={t('applying.heading')} body={t('applying.body')} />
                    <RetryingNotice run={phase.run} />
                </div>
            );
        case 'applied':
            return <AppliedStep run={phase.run} entity={target.entity} onDone={onClose} />;
        case 'error':
            return (
                <div className="flex flex-col gap-3">
                    <Notice tone="danger">
                        {phase.message}
                        {phase.code ? (
                            <span className="mt-1 block font-mono text-[11px] text-muted-foreground">
                                {t('errors.code', { code: phase.code })}
                            </span>
                        ) : null}
                    </Notice>
                    <Footer>
                        <Button variant="outline" onClick={onClose}>
                            {t('actions.close')}
                        </Button>
                        {phase.retry ? (
                            <Button onClick={phase.retry}>{t('actions.retry')}</Button>
                        ) : null}
                    </Footer>
                </div>
            );
    }
}

/** Dispara `onApplied` una sola vez por corrida aplicada. */
function useAppliedCallback(runId: string | null, onApplied?: () => void) {
    const notified = useRef<string | null>(null);
    useEffect(() => {
        if (runId === null || notified.current === runId) return;
        notified.current = runId;
        onApplied?.();
    }, [runId, onApplied]);
}

function Footer({ children }: { children: ReactNode }) {
    return <div className="mt-2 flex justify-end gap-2">{children}</div>;
}

const NOTICE_TONE = {
    danger: 'border-destructive/40 bg-destructive/10',
    warning: 'border-warning-soft-border bg-warning-soft',
    info: 'border-border bg-muted/40',
} as const;

function Notice({ tone, children }: { tone: keyof typeof NOTICE_TONE; children: ReactNode }) {
    const Icon = tone === 'info' ? Info : AlertTriangle;
    return (
        <div
            role={tone === 'danger' ? 'alert' : 'status'}
            className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]',
                NOTICE_TONE[tone],
            )}
        >
            <Icon
                className={cn(
                    'mt-[1px] size-4 flex-none',
                    tone === 'danger' ? 'text-destructive' : 'text-warning',
                    tone === 'info' && 'text-muted-foreground',
                )}
                aria-hidden
            />
            <p>{children}</p>
        </div>
    );
}

function Progress({ heading, body, extra }: { heading: string; body: string; extra?: string }) {
    return (
        <div role="status" className="flex flex-col items-center gap-1 pb-4 text-center">
            <Loading text={heading} className="pb-1" />
            <p className="max-w-md text-sm text-muted-foreground">{body}</p>
            {extra ? <p className="text-xs text-muted-foreground">{extra}</p> : null}
        </div>
    );
}

/** Página que falló y espera su siguiente intento de SQS; nada si la corrida no ha fallado. */
function RetryingNotice({ run }: { run: ReconciliationRun | undefined }) {
    const { t } = useTranslation('reconciliation');
    if (run?.status !== ReconciliationRunStatusEnum.RUNNING || !run.lastErrorCode) return null;
    const heading = isZentralDown(run.lastErrorCode)
        ? t('retrying.zentralDown')
        : t('retrying.failed');
    const attempt =
        run.attempt !== null && run.attempt !== undefined
            ? t('retrying.attempt', {
                  attempt: run.attempt,
                  maxAttempts: run.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
              })
            : null;
    return (
        <Notice tone="warning">
            {[heading, attempt].filter(Boolean).join(' · ')}. {t('retrying.next')}
        </Notice>
    );
}

function Calculating({
    run,
    onCancel,
}: {
    run: ReconciliationRun | undefined;
    onCancel: () => void;
}) {
    const { t } = useTranslation('reconciliation');
    const pending = run && 'storesPending' in run ? run.storesPending : undefined;
    const extra = run
        ? [
              t('calculating.pages', { count: run.pagesProcessed }),
              pending ? t('calculating.storesPending', { count: pending }) : null,
          ]
              .filter(Boolean)
              .join(' · ')
        : undefined;
    return (
        <div className="flex flex-col gap-2">
            <Progress
                heading={t('calculating.heading')}
                body={t('calculating.body')}
                extra={extra}
            />
            <RetryingNotice run={run} />
            <Footer>
                <Button variant="outline" onClick={onCancel}>
                    {t('actions.cancel')}
                </Button>
            </Footer>
        </div>
    );
}

function CountCards({
    counts,
    dimmed,
}: {
    counts: ReconciliationRunCounts;
    dimmed?: PreviewGroup;
}) {
    const { t } = useTranslation('reconciliation');
    return (
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {PREVIEW_GROUPS.map((group) => (
                <div
                    key={group}
                    className={cn(
                        'rounded-md border border-border px-3 py-2',
                        dimmed === group && 'opacity-60',
                    )}
                >
                    <dt className="text-[11px] text-muted-foreground">{t(`groups.${group}`)}</dt>
                    <dd className="text-lg font-bold tabular-nums">{counts[group]}</dd>
                </div>
            ))}
        </dl>
    );
}

/** Avisos de renglones sin leer y tiendas sin revisar; vale para la vista previa y el resumen. */
function RunNotes({ run }: { run: ReconciliationRun }) {
    const { t } = useTranslation('reconciliation');
    const notFound = 'storesNotFound' in run ? (run.storesNotFound ?? 0) : 0;
    const aborted = 'storesAborted' in run ? (run.storesAborted ?? 0) : 0;
    return (
        <>
            {run.counts.skipped > 0 ? (
                <Notice tone="warning">{t('skipped', { count: run.counts.skipped })}</Notice>
            ) : null}
            {notFound > 0 ? (
                <Notice tone="warning">{t('storesNotFound', { count: notFound })}</Notice>
            ) : null}
            {aborted > 0 ? (
                <Notice tone="warning">{t('storesAborted', { count: aborted })}</Notice>
            ) : null}
        </>
    );
}

function PreviewStep({
    run,
    entity,
    mode,
    storeNames,
    onCancel,
    onApply,
}: {
    run: ReconciliationRun;
    entity: ReconciliationEntityEnum;
    mode: RetailIntegrationModeEnum | null;
    storeNames: ReadonlyMap<string, string>;
    onCancel: () => void;
    onApply: () => void;
}) {
    const { t } = useTranslation('reconciliation');
    const [applying, setApplying] = useState(false);
    const { counts } = run;
    const deactivates = appliesDeactivations(mode);
    const total = changesToApply(counts, mode);
    const groups = PREVIEW_GROUPS.filter((group) => counts[group] > 0);

    return (
        <div className="flex flex-col gap-3">
            {total === 0 ? (
                <div className="py-2">
                    <p className="font-semibold">{t('noChanges.heading')}</p>
                    <p className="text-sm text-muted-foreground">
                        {counts.deactivated > 0 && !deactivates
                            ? t('noChanges.softOnly')
                            : t('noChanges.body')}
                    </p>
                </div>
            ) : (
                <p className="font-semibold">{t('preview.heading')}</p>
            )}
            <CountCards counts={counts} dimmed={deactivates ? undefined : 'deactivated'} />
            {!deactivates ? <Notice tone="info">{t('soft')}</Notice> : null}
            {deactivates && counts.deactivated > 0 ? (
                <Notice tone="warning">
                    {t(`impact.${entity}`, { count: counts.deactivated })}
                </Notice>
            ) : null}
            {groups.length > 0 ? (
                <div className="flex max-h-72 flex-col gap-3 overflow-y-auto pr-1">
                    {groups.map((group) => (
                        <PreviewList
                            key={group}
                            group={group}
                            items={run.preview?.[group] ?? []}
                            counted={counts[group]}
                            skippedBySoft={group === 'deactivated' && !deactivates}
                            storeNames={storeNames}
                        />
                    ))}
                </div>
            ) : null}
            <RunNotes run={run} />
            {total > 0 ? (
                <p className="text-xs text-muted-foreground">{t('preview.latest')}</p>
            ) : null}
            <Footer>
                {total === 0 ? (
                    <Button variant="outline" onClick={onCancel}>
                        {t('actions.close')}
                    </Button>
                ) : (
                    <>
                        <Button variant="outline" onClick={onCancel}>
                            {t('actions.cancel')}
                        </Button>
                        <Button
                            disabled={applying}
                            onClick={() => {
                                setApplying(true);
                                onApply();
                            }}
                        >
                            {t('actions.apply', { count: total })}
                        </Button>
                    </>
                )}
            </Footer>
        </div>
    );
}

function PreviewList({
    group,
    items,
    counted,
    skippedBySoft,
    storeNames,
}: {
    group: PreviewGroup;
    items: readonly ReconciliationPreviewItem[];
    counted: number;
    skippedBySoft: boolean;
    storeNames: ReadonlyMap<string, string>;
}) {
    const { t } = useTranslation('reconciliation');
    const hidden = hiddenRows(items.length, counted);
    const headingId = `reconcile-preview-${group}`;
    return (
        <section aria-labelledby={headingId}>
            <h3 id={headingId} className="mb-1 text-[12px] font-semibold">
                {t(`groups.${group}`)} · {counted}
                {skippedBySoft ? (
                    <span className="font-normal text-muted-foreground">
                        {' '}
                        ({t('preview.softSkipped')})
                    </span>
                ) : null}
            </h3>
            <ul className="flex flex-col divide-y divide-border/60 rounded-md border border-border text-[12px]">
                {sortPreviewItems(items).map((item) => (
                    <PreviewRow
                        key={`${item.id}:${item.storeId ?? ''}`}
                        item={item}
                        storeNames={storeNames}
                    />
                ))}
                {hidden > 0 ? (
                    <li className="px-2.5 py-1.5 italic text-muted-foreground">
                        {t('preview.more', { count: hidden })}
                    </li>
                ) : null}
            </ul>
        </section>
    );
}

function PreviewRow({
    item,
    storeNames,
}: {
    item: ReconciliationPreviewItem;
    storeNames: ReadonlyMap<string, string>;
}) {
    const { t } = useTranslation('reconciliation');
    const name = previewItemName(item);
    const fields = previewFieldKeys(item.fields).map((field) =>
        t(`fields.${field}`, { defaultValue: field }),
    );
    const store = item.storeId ? (storeNames.get(item.storeId) ?? null) : null;
    return (
        <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-2.5 py-1.5">
            <span className="font-medium">{name}</span>
            {store ? <span className="text-muted-foreground">· {store}</span> : null}
            {fields.length > 0 ? (
                <span className="ml-auto text-muted-foreground">{fields.join(', ')}</span>
            ) : null}
        </li>
    );
}

function AppliedStep({
    run,
    entity,
    onDone,
}: {
    run: ReconciliationRun;
    entity: ReconciliationEntityEnum;
    onDone: () => void;
}) {
    const { t } = useTranslation('reconciliation');
    return (
        <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 font-semibold">
                <CheckCircle2 className="size-5 text-success" aria-hidden />
                {t('applied.heading')}
            </p>
            <CountCards counts={run.counts} />
            {run.counts.deactivated > 0 ? (
                <p className="text-sm text-muted-foreground">
                    {t(`appliedImpact.${entity}`, { count: run.counts.deactivated })}{' '}
                    {t('applied.body')}
                </p>
            ) : null}
            <RunNotes run={run} />
            <Footer>
                <Button onClick={onDone}>{t('actions.done')}</Button>
            </Footer>
        </div>
    );
}
